import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  DEFAULT_MEDIA_WIDGET_OPTIONS,
  MediaWidgetFileKind,
  MediaWidgetOptions,
  MediaWidgetSource,
  parseMediaWidgetData,
  RawMediaWidgetData,
} from 'fym-shared/src/smartlinks/mediaWidget';
import { SerializedMediaWidget } from 'fym-common/src/db/serializersTypes';
import { MediaBlockPick, pickOfWidget } from './mediaBlockPick';

function optionsOf(widget: SerializedMediaWidget | undefined): MediaWidgetOptions {
  if (widget === undefined) {
    return { ...DEFAULT_MEDIA_WIDGET_OPTIONS };
  }
  const { autoplay, loop, controls, shape, fit, caption, linkUrl } = widget.data;
  return { autoplay, loop, controls, shape, fit, caption, linkUrl };
}

/**
 * The edit state of a Media block in the rail: the source, the picked file and the options. `data`
 * is the stored shape (fym-shared `parseMediaWidgetData`), or null while a field is invalid or no
 * file is picked: Save sends nothing then.
 */
export function useMediaBlockDraft(widget: SerializedMediaWidget | undefined) {
  const intl = useIntl();
  const [source, setSource] = useState<MediaWidgetSource | null>(() => widget?.data.source ?? null);
  const [pick, setPick] = useState<MediaBlockPick | null>(() =>
    widget === undefined ? null : pickOfWidget(intl, widget.data)
  );
  const [options, setOptions] = useState<MediaWidgetOptions>(() => optionsOf(widget));

  const choose = useCallback((next: MediaBlockPick) => {
    setPick(next);
    setSource(next.source);
  }, []);

  const setOption = useCallback(<K extends keyof MediaWidgetOptions>(key: K, value: MediaWidgetOptions[K]) => {
    setOptions((current) => ({ ...current, [key]: value }));
  }, []);

  const data = useMemo<RawMediaWidgetData | null>(
    () => (source === null ? null : parseMediaWidgetData({ ...options, source })),
    [options, source]
  );

  /** The kind of the file now: the Playback options and Fit show only for a video. */
  const kind: MediaWidgetFileKind | null = pick?.kind ?? widget?.data.media?.kind ?? null;

  return { source, pick, choose, options, setOption, data, kind };
}

export type MediaBlockDraft = ReturnType<typeof useMediaBlockDraft>;
